import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertCircle, Info, Loader2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { usePrices } from '../hooks/use-prices'
import { useAssignments } from '../hooks/use-assignments'
import { useRestaurants } from '../hooks/use-restaurants'
import { useProductDetail } from '../hooks/use-product-detail'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { pricesApi } from '@/api/endpoints/prices'
import { ResponsiveTableWrapper } from '@/shared/components/responsive-table-wrapper'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { formatCurrency } from '@/shared/utils/format-currency'
import { ApiError } from '@/api/types'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

interface PricesTabProps {
  sku: string
}

export function PricesTab({ sku }: PricesTabProps) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const { data: product } = useProductDetail(sku)
  // A merged duplicate (CHG-2026-09-duplicate-product-merge) is not independently sellable, so
  // its prices are frozen — pricing actions target the primary product instead.
  const isMerged = !!product?.mergedIntoSku
  const canEdit = can(ACTIONS.PRICE_EDIT) && !isMerged
  const [editingRestaurantId, setEditingRestaurantId] = useState<number | null>(
    null,
  )
  const [editValue, setEditValue] = useState('')

  const {
    data: prices,
    isLoading: pricesLoading,
    error: pricesError,
  } = usePrices(sku)
  const { data: assignments, isLoading: assignmentsLoading } =
    useAssignments(sku)
  const { data: restaurants, isLoading: restaurantsLoading } = useRestaurants()

  const setPriceMutation = useMutation({
    mutationFn: ({
      restaurantId,
      amountCents,
    }: {
      restaurantId: number
      amountCents: number
    }) => pricesApi.set(sku, restaurantId, amountCents),
    onSuccess: () => {
      toast.success(tProducts('toast.priceUpdated'))
      queryClient.invalidateQueries({ queryKey: ['products', sku, 'prices'] })
      setEditingRestaurantId(null)
      setEditValue('')
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(tProducts('toast.priceUpdateFailed'))
      }
    },
  })

  const handleEditPrice = (restaurantId: number, currentCents?: number) => {
    if (!canEdit) return
    setEditingRestaurantId(restaurantId)
    setEditValue(
      currentCents !== undefined ? (currentCents / 100).toFixed(2) : '',
    )
  }

  const handleSavePrice = (restaurantId: number) => {
    const euros = parseFloat(editValue)
    if (isNaN(euros) || euros < 0) {
      toast.error(tProducts('prices.invalidPrice'))
      return
    }
    const amountCents = Math.round(euros * 100)
    setPriceMutation.mutate({ restaurantId, amountCents })
  }

  const handleCancelEdit = () => {
    setEditingRestaurantId(null)
    setEditValue('')
  }

  const handleKeyPress = (e: React.KeyboardEvent, restaurantId: number) => {
    if (e.key === 'Enter') {
      handleSavePrice(restaurantId)
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  if (pricesLoading || assignmentsLoading || restaurantsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (pricesError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('error')}</AlertTitle>
        <AlertDescription>
          {tProducts('prices.loadErrorDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  if ((restaurants?.items.length ?? 0) === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tProducts('prices.noRestaurantsTitle')}</AlertTitle>
        <AlertDescription>
          {tProducts('prices.noRestaurantsDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const assignedRestaurantIds = new Set(
    assignments?.map((a) => a.restaurantId) || [],
  )

  const priceMap = new Map(
    prices?.map((p) => [p.restaurantId, p.amountCents]) || [],
  )

  // Only show restaurants where product is assigned
  const assignedRestaurants =
    restaurants?.items.filter((r) => assignedRestaurantIds.has(r.id)) ?? []

  if (assignedRestaurants.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{tProducts('prices.notAssignedTitle')}</AlertTitle>
        <AlertDescription>
          {tProducts('prices.notAssignedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        {tProducts('prices.description')}
      </p>

      <ResponsiveTableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tProducts('prices.tableRestaurant')}</TableHead>
              <TableHead>{tProducts('prices.tableCode')}</TableHead>
              <TableHead className="text-right">
                {tProducts('prices.tablePrice')}
              </TableHead>
              <TableHead className="text-right">
                {tProducts('prices.tableActions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignedRestaurants.map((restaurant) => {
              const currentCents = priceMap.get(restaurant.id)
              const isEditing = editingRestaurantId === restaurant.id

              return (
                <TableRow key={restaurant.id}>
                  <TableCell className="font-medium">
                    {restaurant.name}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {restaurant.code}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e, restaurant.id)}
                        className="w-32 ml-auto"
                        placeholder={tProducts('prices.placeholder')}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : currentCents !== undefined ? (
                      <span className="font-mono">
                        {formatCurrency(currentCents)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {tProducts('prices.notSet')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleSavePrice(restaurant.id)}
                          disabled={setPriceMutation.isPending || !canEdit}
                        >
                          {setPriceMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            tCommon('save')
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={setPriceMutation.isPending || !canEdit}
                        >
                          {tCommon('cancel')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleEditPrice(restaurant.id, currentCents)
                        }
                        disabled={!canEdit}
                      >
                        {currentCents !== undefined
                          ? tCommon('edit')
                          : tProducts('prices.setPrice')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </ResponsiveTableWrapper>
    </div>
  )
}
